import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { addDaysToDate, diffBetweenDays } from '@app/core/date-utils';

import {
  IGetPatientListByRequest,
  IGetPatientListRequest,
  mergeSearchByUserFromStoredSearchType,
  SearchType,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { MsgboxService } from '@app/shared/msgbox';
import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';

export interface IRequestSearchInfo {
  maxSearchDays: number;
  previousSearch: IGetPatientListByRequest;
  defaultSearch: IGetPatientListByRequest;
}

@Component({
  selector: 'app-search-by-request',
  templateUrl: './search-by-request.component.html',
  styleUrls: ['./search-by-request.component.css'],
})
export class SearchByRequestComponent implements OnDestroy, OnInit {
  @Input()
  public searchInfo: IRequestSearchInfo;

  @Output()
  public onSearch: EventEmitter<IGetPatientListByRequest>;

  public sisifoUserHasRelatedDoctor$: Subject<boolean>;
  public sisifoUserHasRelatedCenter$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  public form: FormGroup;

  public today = new Date();

  private destroyed$ = new Subject<void>();

  private DISABLE_CONDITIONS = {
    labRequestFrom: ['centerRequestFrom', 'centerRequestTo'],
    labRequestTo: ['centerRequestFrom', 'centerRequestTo'],
    centerRequestFrom: ['labRequestFrom', 'labRequestTo'],
    centerRequestTo: ['labRequestFrom', 'labRequestTo'],
  };

  constructor(
    private _userService: UsersService,
    private msgbox: MsgboxService
  ) {
    this.onSearch = new EventEmitter();

    this.form = new FormGroup({
      labRequestFrom: new FormControl(),
      labRequestTo: new FormControl(),
      centerRequestFrom: new FormControl(),
      centerRequestTo: new FormControl(),
      extractionFrom: new FormControl(),
      extractionTo: new FormControl(),
      searchByUser: new FormControl(false),
    });

    this.form.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.enableDisableFields();
      });
  }
  ngOnInit(): void {
    this.getSisifoUserHasRelatedDoctor();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchInfo'].currentValue) {
      const raw = this.searchInfo.previousSearch || this.searchInfo.defaultSearch;
      this.form.patchValue(mergeSearchByUserFromStoredSearchType(raw as Record<string, unknown>), {
        emitEvent: false,
      });
      this.enableDisableFields();
    }
  }

  enableDisableFields(): void {
    Object.keys(this.DISABLE_CONDITIONS).forEach((key) => {
      const conditionArray = this.DISABLE_CONDITIONS[key];
      const field = this.form.get(key);
      const conditionAccomplished = conditionArray.some(
        (condition) => !!this.form.get(condition).value
      );

      if (conditionAccomplished) {
        field.disable({ emitEvent: false });
      } else {
        field.enable({ emitEvent: false });
      }
    });

    // Lógica para habilitar todos cuando ninguno tiene valor
    const value = this.form.value;
    if (
      !value.labRequestFrom &&
      !value.labRequestTo &&
      !value.centerRequestFrom &&
      !value.centerRequestTo
    ) {
      this.form.get('labRequestFrom').enable({ emitEvent: false });
      this.form.get('labRequestTo').enable({ emitEvent: false });
      this.form.get('centerRequestFrom').enable({ emitEvent: false });
      this.form.get('centerRequestTo').enable({ emitEvent: false });
    }

    // Regla específica: Si solo labRequestFrom (sin labRequestTo) o solo centerRequestFrom (sin centerRequestTo),
    // deshabilitar extractionFrom y extractionTo
    if (
      (value.labRequestFrom && !value.labRequestTo) ||
      (value.centerRequestFrom && !value.centerRequestTo)
    ) {
      this.form.get('extractionFrom').disable({ emitEvent: false });
      this.form.get('extractionTo').disable({ emitEvent: false });
    } else {
      // En otros casos, habilitar los campos de extracción
      this.form.get('extractionFrom').enable({ emitEvent: false });
      this.form.get('extractionTo').enable({ emitEvent: false });
    }

    // WIR-RF-02.01.13: valor por defecto "Fecha extracción desde/hasta" solo si hay rango petición o centro
    const hasRequestRange =
      (value.labRequestFrom && value.labRequestTo) ||
      (value.centerRequestFrom && value.centerRequestTo);
    const extractionEmpty =
      !value.extractionFrom && !value.extractionTo;
    if (
      hasRequestRange &&
      extractionEmpty &&
      this.searchInfo?.maxSearchDays != null
    ) {
      this.form.patchValue(
        {
          extractionFrom: addDaysToDate(
            new Date(),
            -this.searchInfo.maxSearchDays
          ),
          extractionTo: new Date(),
        },
        { emitEvent: false }
      );
    }
  }

  /***
   * Format date to dd/MM/yyyy
   * @param date
   * @returns {string}
   */
  formatDate(date: Date): string {
    if (!date) {
      return '';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  buildPayload(value: any): IGetPatientListRequest {
    const result = {
      ...value,
      searchType: value.searchByUser ? SearchType.User : SearchType.Center,
      extractionFrom: value.extractionFrom
        ? this.formatDate(value.extractionFrom)
        : null,
      extractionTo: value.extractionTo
        ? this.formatDate(value.extractionTo)
        : null,
    };
    delete result.searchByUser;
    return result;
  }

  getSisifoUserHasRelatedDoctor(): void {
    this.sisifoUserHasRelatedDoctor$ = new Subject<boolean>();
    this._userService.getSisifoUserData().subscribe((userData) => {
      this.sisifoUserHasRelatedDoctor$.next(!!userData.arztNr);
      this.sisifoUserHasRelatedCenter$.next(!!userData.centers?.length);
    });
  }

  public search(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    // check if labRequestFrom and labRequestTo are set, then labRequestTo must be greater than labRequestFrom
    if (
      this.form.value.labRequestFrom &&
      this.form.value.labRequestTo &&
      +this.form.value.labRequestFrom > +this.form.value.labRequestTo
    ) {
      this.msgbox.showError(
        'WEB_RESULTS.ERROR.LAB_REQUEST_FROM_GREATER_THAN_LAB_REQUEST_TO'
      );
      return;
    }

    // check if centerRequestFrom and centerRequestTo are set, then centerRequestTo must be greater than centerRequestFrom
    if (
      this.form.value.centerRequestFrom &&
      this.form.value.centerRequestTo &&
      +this.form.value.centerRequestFrom > +this.form.value.centerRequestTo
    ) {
      this.msgbox.showError(
        'WEB_RESULTS.ERROR.CENTER_REQUEST_FROM_GREATER_THAN_CENTER_REQUEST_TO'
      );
      return;
    }

        if (this.form.value.extractionTo && this.form.value.extractionFrom) {
          if (
            diffBetweenDays(
              this.form.value.extractionTo,
              this.form.value.extractionFrom
            ) > this.searchInfo.maxSearchDays
          ) {
            this.msgbox.showError('WEB_RESULTS.ERROR.MAX_EXTRACTION_RANGE', {
              value: this.searchInfo.maxSearchDays,
            });
            return;
          }
        }

    // remove dates if only search by from
    const value = this.form.value as IGetPatientListByRequest;
    if (
      (value.centerRequestFrom && !value.centerRequestTo) ||
      (value.labRequestFrom && !value.labRequestTo)
    ) {
      delete value.extractionFrom;
      delete value.extractionTo;
      this.form.patchValue(value, { emitEvent: false });
    }

    this.onSearch.next(this.buildPayload(value));
  }

  public clear(): void {
    this.form.reset();
    this.form.patchValue(this.searchInfo.defaultSearch, { emitEvent: false });
    this.enableDisableFields();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
